<script setup lang="ts">
import { Plus, Store } from 'lucide-vue-next'

import useNotification from '@/composables/notification'

import AppTable from '@/components/ui/Table/AppTable.vue'

import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import BaseIcon from '@/components/base/BaseIcon.vue'
import MultiSelectStores from '@/components/ui/MultiSelectStores.vue'

import useOffer from '@/composables/offer'
import useContentScopeList, { useContentScopeCriteria } from '@/composables/contentScopeList'
import useLocalStorage from '@/composables/useLocalStorage'
import useStore from '@/composables/store'
import { getRewardTypeLabel } from '@/components/rewards/utils/rewardHelpers'
import { getOfferStatus } from '@/utils/offerHelpers'

import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { RowData } from '@/components/ui/Table/interfaces'
import type { Offer } from '@/types/types'

const { t } = useI18n()
const router = useRouter()
const notification = useNotification()
const { permissions } = useLocalStorage()
const { fetchOffers, fetchOffersTotal, endOffer } = useOffer()
const { selectedStore } = useStore()

const isLoading = ref(true)
const isFetching = ref(false)
const offers = ref<Offer[]>([])

const pagination = ref({
  currentPage: 1,
  lastPage: 1,
  total: 0,
  pageSize: 50
})

const canCreateOffer = computed(() => permissions.value['campaign.create'])

const pageTitle = computed(() => ({
  title: 'pages.offer.list.title'
}))

const storeFilter = ref<number[]>(selectedStore.value ? [selectedStore.value] : [])

const offerCreateRoute = { name: 'OfferCreate' } as const

interface OfferRowData {
  name: {
    title: string
    subtitle: string
  }
  benefitType: {
    label: string
  }
  stores: {
    count: number
    label: string
  }
  status: {
    value: 'active' | 'scheduled' | 'expired'
    label: string
  }
  raw: Offer
}

const getOfferRowData = (data: unknown): OfferRowData => data as OfferRowData

const statusChipColor: Record<OfferRowData['status']['value'], ChipColor> = {
  active: 'green',
  scheduled: 'yellow',
  expired: 'red'
}

const OFFERS_PAGE_SIZE = 1000

const contentScope = useContentScopeList('offer', {
  fetchTotal: fetchOffersTotal,
  reload: () => fetchData()
})

const { origin, createdBy } = useContentScopeCriteria(contentScope)
const filterCriteria = computed(() => [origin.value, createdBy.value])

const searchText = ref('')

const hasListContent = computed(() => offers.value.length > 0 || contentScope.hasActiveFilters.value)
const hasNarrowedList = computed(
  () => contentScope.hasActiveFilters.value || Boolean(searchText.value.trim()) || storeFilter.value.length > 0
)

const matchesStores = (offer: Offer) => {
  if (!storeFilter.value.length) return true
  const storeIds = (offer.storeIds && offer.storeIds.length > 0 ? offer.storeIds : offer.reward?.storeIds) ?? []
  if (!storeIds.length) return true
  return storeIds.some((id) => storeFilter.value.includes(id))
}

const matchesSearch = (offer: Offer) => {
  const query = searchText.value.trim().toLowerCase()
  return !query || offer.name.toLowerCase().includes(query)
}

const filteredOffers = computed(() => offers.value.filter((offer) => matchesStores(offer) && matchesSearch(offer)))

const resetListFilters = () => {
  searchText.value = ''
  storeFilter.value = []
  contentScope.resetFilters()
}

const columns = computed(() => ({
  name: {
    label: t('pages.offer.list.table.thead.name'),
    type: 'customCell'
  },
  benefitType: {
    label: t('pages.offer.list.table.thead.benefitType'),
    type: 'customCell'
  },
  status: {
    label: t('pages.offer.list.table.thead.status'),
    type: 'customCell'
  },
  origin: {
    label: t('contentScope.origin'),
    type: 'customCell'
  },
  stores: {
    label: t('pages.offer.list.table.thead.stores'),
    type: 'customCell'
  },
  creator: {
    label: t('contentScope.filters.createdBy'),
    type: 'customCell'
  }
}))

const rows = computed(() =>
  filteredOffers.value.map((offer) => {
    const status = getOfferStatus(offer.startDate, offer.endDate)

    const storeCount =
      (offer.storeIds && offer.storeIds.length > 0 ? offer.storeIds.length : offer.reward?.storeIds?.length) ?? 0

    return {
      id: offer.id,
      data: {
        name: {
          title: offer.name,
          subtitle: offer.description?.trim() || offer.reward?.name || '—'
        },
        benefitType: {
          label: offer.reward?.rewardType
            ? getRewardTypeLabel(offer.reward.rewardType, t)
            : t('pages.offer.list.benefitType.undefined')
        },
        stores: {
          count: storeCount,
          label:
            storeCount === 0
              ? t('pages.offer.list.stores.none')
              : t('pages.offer.list.stores.count', { count: storeCount })
        },
        status: {
          value: status,
          label: t(`pages.offer.list.status.${status}`)
        },
        raw: offer
      }
    }
  })
)

watch(
  filteredOffers,
  (value) => {
    pagination.value.total = value.length
  },
  { immediate: true }
)

const fetchData = async () => {
  try {
    isFetching.value = true
    const data = await fetchOffers({ pageSize: OFFERS_PAGE_SIZE, ...contentScope.params.value })
    offers.value = data || []
    pagination.value.total = filteredOffers.value.length
  } catch {
    offers.value = []
    notification.create('error', { text: t('pages.offer.list.fetchError') })
  } finally {
    isLoading.value = false
    isFetching.value = false
  }
}

const goToDetails = (row: RowData) => {
  if (!row?.id) return
  router.push({ name: 'OfferDetail', params: { offerId: row.id } })
}

const changePage = (page: number) => {
  pagination.value.currentPage = page
}

const handleEndOffer = async (offer: Offer) => {
  try {
    await endOffer(offer.id)
    notification.create('success', { text: t('pages.offer.list.endSuccess') })
    await fetchData()
  } catch {
    notification.create('error', {
      text: t('pages.offer.list.endError')
    })
  }
}

onMounted(async () => {
  await fetchData()
})
</script>

<template>
  <div>
    <TheLoadingPage v-if="isLoading" />

    <div v-else>
      <TheListHeader :title="t(pageTitle.title)">
        <template #actions>
          <RouterLink v-if="canCreateOffer" :to="offerCreateRoute">
            <Button variant="default-primary">
              <Plus />
              {{ t('pages.offer.list.cta.create') }}
            </Button>
          </RouterLink>
        </template>
      </TheListHeader>

      <div class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ListToolbar v-if="hasListContent" class="mb-4" :criteria="filterCriteria">
          <template #leading>
            <ListSearchInput v-model="searchText" :placeholder="t('contentScope.filters.search')" />
            <MultiSelectStores v-model="storeFilter" :has-label="false" />
          </template>
        </ListToolbar>

        <ContentScopeEmptyState
          v-if="rows.length === 0 && !isFetching && hasNarrowedList"
          :is-mine-view="contentScope.isMineView.value"
          :subject="contentScope.subject"
          @reset="resetListFilters"
        />

        <Card v-else-if="rows.length === 0 && !isFetching" class="mt-2">
          <CardContent class="flex flex-col items-center justify-center gap-4 py-10">
            <div class="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <BaseIcon name="IconoirGift" class="h-7 w-7 text-primary" />
            </div>
            <div class="space-y-1 text-center">
              <p class="text-base font-semibold text-foreground">
                {{ t('pages.offer.list.emptyState.title') }}
              </p>
              <p class="text-sm text-muted-foreground">
                {{ t('pages.offer.list.emptyState.description') }}
              </p>
            </div>
            <RouterLink v-if="canCreateOffer" :to="offerCreateRoute">
              <Button variant="default-primary">
                {{ t('pages.offer.list.emptyState.cta') }}
              </Button>
            </RouterLink>
          </CardContent>
        </Card>

        <AppTable
          v-else
          class="mt-2"
          :columns="columns"
          :rows="rows"
          :pagination="pagination"
          :is-loading="isFetching"
          row-classes="cursor-pointer"
          @update:change-page="changePage"
          @row-clicked="goToDetails"
        >
          <template #td-name="{ data }">
            <div class="w-[320px]">
              <p class="truncate text-sm font-medium leading-5 text-foreground">
                {{ getOfferRowData(data).name.title || '-' }}
              </p>
              <p class="mt-1 truncate text-sm text-muted-foreground">
                {{ getOfferRowData(data).name.subtitle || '-' }}
              </p>
            </div>
          </template>

          <template #th-origin>
            <ContentScopeColumnHeader subject="offer" />
          </template>
          <template #td-origin="{ data }">
            <ContentScopeBadge :scope="getOfferRowData(data).raw.scope" />
          </template>

          <template #td-creator="{ data }">
            <ContentScopeCreator :creator="getOfferRowData(data).raw.creator" />
          </template>

          <template #td-benefitType="{ data }">
            <BaseChip :text="getOfferRowData(data).benefitType.label" />
          </template>

          <template #td-stores="{ data }">
            <BaseChip :text="getOfferRowData(data).stores.label">
              <template #leading>
                <Store />
              </template>
            </BaseChip>
          </template>

          <template #td-status="{ data }">
            <BaseChip
              :color="statusChipColor[getOfferRowData(data).status.value]"
              :text="getOfferRowData(data).status.label"
            />
          </template>

          <template #row-actions="{ row }">
            <div class="flex items-center gap-2 pr-4">
              <Button
                v-if="getOfferRowData(row.data).status.value === 'active'"
                size="sm"
                variant="ghost"
                class="text-xs"
                @click.stop="handleEndOffer(getOfferRowData(row.data).raw)"
              >
                {{ t('pages.offer.list.actions.end') }}
              </Button>
            </div>
          </template>
        </AppTable>
      </div>
    </div>
  </div>
</template>
